import hash from "@emotion/hash"
import { useEnv } from "contexts/Env"
import { useLocale } from "contexts/Locale"
import { useUser } from "contexts/User"
import { useLayoutEffect } from "react"

let adobeLaunchURL = ""

// Helper function to generate timestamp in format "day way hh:mm:ss.ms"
const getTimestamp = () => {
  const now = new Date()
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
  const day = days[now.getDay()]
  const hours = now.getHours().toString().padStart(2, "0")
  const minutes = now.getMinutes().toString().padStart(2, "0")
  const seconds = now.getSeconds().toString().padStart(2, "0")
  const milliseconds = now.getMilliseconds().toString().padStart(3, "0")
  return `${day} ${hours}:${minutes}:${seconds}.${milliseconds}`
}

/* Description of the file
 This file is used to trigger the Adobe Launch event.

  The function waTriggerEvent is used to trigger the event.
  The function pushToDataLayer is used to push data to the dataLayer.
  The function fireActionTriggerCustomEvent is used to dispatch custom event to the browser.

  First of all the launchURL triggers the Adobe Launch script.
  The function load is used to load the script.

  See each function for further details.
 */

export const load = async () => {
  /*
  This function is used to load the Adobe Launch script.
  It is used to load the script only once.
  */
  const launchURL = adobeLaunchURL
  const scriptId = "wa" + hash(launchURL)
  const launchPromiseName = `__${scriptId}`

  //  console.log(`${getTimestamp()} Adobe Launch: Starting to load script`, { launchURL, scriptId })

  const node = (() => {
    if (document.getElementById(scriptId)) {
      //      console.log(`${getTimestamp()} Adobe Launch: Script already exists, reusing existing node`, scriptId)
      return scriptId
    }
    //    console.log(`${getTimestamp()} Adobe Launch: Creating new script node`, { src: launchURL })
    return document.head.appendChild(
      Object.assign(document.createElement("script"), {
        src: launchURL,
        defer: true,
        async: true,
        id: scriptId,
      })
    )
  })()

  const launchPromise = (global[launchPromiseName] ??= new Promise((resolve, reject) => {
    node.addEventListener(
      "load",
      () => {
        //        console.log(`${getTimestamp()} Adobe Launch: Script loaded successfully`, launchURL)
        resolve()
      },
      { once: true }
    )
    node.addEventListener(
      "error",
      err => {
        //        console.error(`${getTimestamp()} Adobe Launch: Script failed to load`, { launchURL, error: err })
        reject(err)
      },
      { once: true }
    )
  })
    .then(() => {
      //      console.log(`${getTimestamp()} Adobe Launch: Waiting 300ms after script load`)
      return new Promise(resolve => setTimeout(resolve, 300))
    })
    .then(() => {
      //      console.log(`${getTimestamp()} Adobe Launch: Script initialization complete`)
    })
    .catch(err => {
      //      console.error(`${getTimestamp()} Adobe Launch: Load process failed`, err)
    }))

  const consentPromise = (global["__consentready"] ??= new Promise(resolve => {
    const interval = setInterval(() => {
      try {
        const hasConsent = window._satellite.getVar("CC-Analyticsconsent")
        // console.log(`${getTimestamp()} Adobe Launch: Consent has been read (%s)`, hasConsent)

        if (hasConsent !== undefined) {
          //          console.log(`${getTimestamp()} Adobe Launch: Consent has been defined (%s)`, hasConsent)
          clearInterval(interval)
          resolve()
        }
      } catch {}
    }, 300)
  }))

  const airshipPromise = (global["__airshipID"] ??= new Promise(resolve => {
    const interval = setInterval(() => {
      try {
        const hasAirshipID = window.airshipID
        if (hasAirshipID !== undefined) {
          clearInterval(interval)
          resolve()
        }
      } catch {}
    }, 300)
  }))

  return Promise.all([launchPromise, consentPromise /*, airshipPromise*/])
}

let tempOverriddenPageName = null
export const pushToDataLayer = (data, eventName, eventType, pageName, pageURL, options = {}) => {
  /*
  This function is used to push data to the dataLayer.
  The dataLayer is read by the Adobe Launch script.
  It is used to push data to the dataLayer only once.

  NB: In development mode, useEffect are run twice, so the dataLayer is pushed twice.
  */

  if (!process.browser) {
    // we don't want to push data to the dataLayer in SSR
    return
  }

  // get custom event from localStorage then remove custom event from localStorage
  const customEvent = localStorage.getItem("rlx_ddl_custom_event")
  localStorage.removeItem("rlx_ddl_custom_event")
  const customPageName = sessionStorage.getItem("rlx_ddl_custom_pagename")
  sessionStorage.removeItem("rlx_ddl_custom_pagename")

  if (!pageName && customPageName) pageName = customPageName

  return load().then(() => {
    try {
      const statusCode = +(document.documentElement.dataset.status ?? "200")
      if (eventName) {
        /*
          In case of custom event, we want to push the event name to the dataLayer.
          So we get the last element of the dataLayer that has a page attribute, and we push the event name to the customEvents array.
        */
        const lastElement = global.digitalDataLayer[global.digitalDataLayer.length - 1]
        const lastElementCopy = structuredClone(lastElement)

        const lastElementDataCopy = structuredClone(lastElement.data)
        // items to keep from lastElementDataCopy
        const itemsToKeep = ["environment", "location", "page"]

        for (const key in lastElementDataCopy) {
          if (!itemsToKeep.includes(key)) {
            delete lastElementDataCopy[key]
          }
        }

        delete lastElementDataCopy.retailerInfo

        lastElementCopy.event = eventType
        lastElementCopy.data = { ...lastElementDataCopy, ...data }

        const customEvents = []
        if (Array.isArray(eventName)) {
          customEvents.push(...eventName)
        } else {
          customEvents.push(eventName)
        }
        if (customEvent) customEvents.push(customEvent)
        if (customEvents.length > 0) lastElementCopy.data.page.customEvents = customEvents

        lastElementCopy.data.page = { ...lastElementCopy.data.page }

        if (tempOverriddenPageName) {
          // pageName has been modified in the previous push, but it should go back to its normal value now
          lastElementCopy.data.page.pageName = tempOverriddenPageName
          tempOverriddenPageName = null
        }

        if (pageName) lastElementCopy.data.page.pageName = pageName //only for config special pageName
        else if (options?.overrides?.pageName) {
          tempOverriddenPageName = lastElementCopy.data.page.pageName // pageName will be modified in the next push, but should go back to its normal value next
          lastElementCopy.data.page.pageName = options.overrides.pageName(lastElementCopy.data.page.pageName)
        }
        if (pageURL) lastElementCopy.data.page.pageURL = pageURL

        lastElementCopy.data.environment.statusCode = statusCode //update statusCode
        global.digitalDataLayer.push(lastElementCopy)
      } else if (data.page === undefined) {
        /*
          Mostly the case of the initial dataLayer.
          We only have the variables contained at in the initialDataLayer
          (see the component's useEffect at the very bottom)
        */
        const lastElement = structuredClone(global.digitalDataLayer[global.digitalDataLayer.length - 1]) ?? { data: {} }
        delete lastElement.data?.retailerInfo
        const newData = { ...lastElement.data, ...data }
        newData.environment.statusCode = statusCode //update statusCode
        global.digitalDataLayer.push({
          ...lastElement,
          event: eventType,
          data: newData,
        })
      } else {
        /*
          In case of page event, we want to push the page attributes to the dataLayer.
          So we get the last element of the dataLayer that has a page attribute, and we push the page attributes to the dataLayer.
          We also want to get the initial attributes (environment and location) and push them to the dataLayer.
        */

        const lastElement = structuredClone(global.initialDigitalDataLayer)
        const newData = { ...lastElement.data, ...data }
        // add custom event to the page
        if (customEvent) {
          newData.page.customEvents = newData.page.customEvents || []
          newData.page.customEvents.push(customEvent)
        }
        newData.environment.statusCode = statusCode //update statusCode

        if (window.airshipID) {
          newData.environment.airshipID = window.airshipID
        }

        const nextPush = { ...global.initialDigitalDataLayer, data: newData }
        nextPush.data.location.countryCode = global.__rlx?.realCountryCode ?? nextPush.data.location.countryCode
        nextPush.data.location.languageCode = global.__rlx?.locale ?? nextPush.data.location.languageCode

        global.digitalDataLayer.push(nextPush)
      }
    } catch (e) {
      console.error(e)
    }
  })
}

// function to dispatch custom event to the browser
export const fireActionTriggerCustomEvent = data => {
  if (process.browser) {
    document.dispatchEvent(new CustomEvent("event-action-trigger", { detail: data || {} }))
  }
}

export const fireViewEndCustomEvent = () => {
  const event = new CustomEvent("event-view-end")
  const obj = document.querySelector("body")
  obj.dispatchEvent(event)
}

export const storeCustomEvent = ({ eventName }) => {
  // add eventName to the localStorage to be used in the next page (rlx_ddl_custom_event)
  if (process.browser) {
    localStorage.setItem("rlx_ddl_custom_event", eventName)
  }
}

export const storeCustomRetailerInteraction = interaction => {
  if (process.browser) {
    localStorage.setItem("rlx_ddl_retailer_interaction", interaction)
  }
}

export const storeCustomPageName = pageName => {
  // add pageName to the sessionStorage to be used in the next page (rlx_ddl_custom_pagename) if pageName undefined
  if (process.browser) {
    sessionStorage.setItem("rlx_ddl_custom_pagename", pageName)
  }
}

export const waTriggerEvent = ({ eventName, eventType, pageName, pageURL, ...data }, options) => {
  /*
    This is the function called by the components to trigger events.
    It makes sure that the Adobe Launch script is loaded before pushing data to the dataLayer.
    It also makes sure that the initial dataLayer is pushed to the dataLayer before pushing data to the dataLayer via a timeout funcction.
    (see the send function)
  */

  if (!eventName) throw new Error(" WA_Trigger_Event : eventName is necessary")
  let tm = null

  //  console.log(`${getTimestamp()} Adobe Launch: waTriggerEvent called`, { eventName, eventType, pageName, pageURL })

  const detailsCustomEvent = { ...data }

  function send() {
    if (process.browser && (document.location.hostname !== "www.rolex.com" || document.location.pathname !== "www.rolex.cn")) {
      // console.log("wawa", JSON.stringify(detailsCustomEvent, null, "\t"))
    }
    const ddlReady =
      process.browser &&
      global.digitalDataLayer &&
      global.digitalDataLayer.length > 0 &&
      global.digitalDataLayer[global.digitalDataLayer.length - 1]?.data?.location

    if (!process.browser || !global.digitalDataLayer) {
      //      console.log(`${getTimestamp()} Adobe Launch: Browser or digitalDataLayer not available, skipping send`)
      return
    }

    clearTimeout(tm)
    if (!ddlReady) {
      //      console.log(`${getTimestamp()} Adobe Launch: Data layer not ready, retrying in 1000ms`)
      tm = setTimeout(send, 1000)
    } else {
      //      console.log(`${getTimestamp()} Adobe Launch: Data layer ready, pushing to dataLayer`, { eventName, eventType })
      pushToDataLayer(data, eventName, eventType, pageName, pageURL, options)
      fireActionTriggerCustomEvent(detailsCustomEvent)
    }
  }

  //  console.log(`${getTimestamp()} Adobe Launch: Initiating script load before sending event`)
  load().then(() => {
    //    console.log(`${getTimestamp()} Adobe Launch: Script load completed, calling send function`)
    send()
  })
}

export default function DigitalDataLayer({ children, initialState, version }) {
  // const { version } = initialState || {}

  const { realCountryCode } = useUser()
  const locale = useLocale()
  const env = useEnv()
  adobeLaunchURL = env.adobeLaunchURL
  const handleBodyEvent = () => {
    const [lastpageload] = window.digitalDataLayer?.filter(o => o.event === "Page Load")?.reverse()
    if (!lastpageload) return
    window.digitalDataLayer.push(JSON.parse(JSON.stringify(lastpageload)))
    console.log("ADDED PAGELOAD TO DATALAYER")
    console.log("digitalDataLayer", global.digitalDataLayer)
  }

  useLayoutEffect(() => {
    document.body.addEventListener("quantcast:consent", handleBodyEvent)

    return () => {
      document.body.removeEventListener("quantcast:consent", handleBodyEvent)
    }
  })

  // useEffect(() => {
  // ignore wishlist because we want to send the event from the wishlist component with extra params
  if (process.browser) {
    // dataLayer, only FE, resets at every page
    const languageCode = locale?.current?.codes?.www
    global.digitalDataLayer ??= []
    global.initialDigitalDataLayer ??= {
      event: "Page Load",
      data: {
        environment: {
          environmentVersion: version, // todo: change version dynamically
          environmentName: env.waEnvironment,
          siteName: global.location.origin,
          statusCode: +(document.documentElement.dataset.status ?? "200"),
        },
        location: {
          countryCode: realCountryCode,
          languageCode: languageCode,
        },
      },
    }
  }
  // }, [])

  return children
}
